import { useContext, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { fetchAuthorEmailLogHistory, fetchSubmissionFields } from 'src/api/queries';

import {
  SubmissionDataContext,
  type ISubmissionDataContext
} from 'src/contexts/SubmissionDataProvider';
import useCurrentContextId from 'src/hooks/useCurrentContextId';

import ListLoader from 'src/components/ListLoader';
import EmailLogHistoryTab from './EmailLogHistoryTab';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import { Pagination } from '@morressier/alembic';

import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { type IRoleType } from '@publishing-engine/domain/models';

const LIMIT = 10;

type EmailLogUser = {
  email: string;
  firstName: string;
  lastName: string;
};

interface IEmailLogProps {
  user?: EmailLogUser;
  roleContext?: IRoleType;
  submissionId?: string;
}

export default function EmailLog({
  user,
  roleContext,
  submissionId
}: IEmailLogProps) {
  const { email, firstName, lastName } = user!;

  const authorName = `${firstName ?? ''} ${lastName ?? ''}`.trim();

  const [page, setPage] = useState<number>(1);
  const offset = (page - 1) * LIMIT;

  const submissionData = useContext(SubmissionDataContext) as
    | ISubmissionDataContext
    | undefined;
  const resolvedSubmissionId = submissionData?.submissionId ?? submissionId;
  const submissionContextId =
    submissionData?.submissionContextId ?? useCurrentContextId();

  const {
    data: fallbackFieldsResponse,
    isLoading: isLoadingFallbackFields
  } = useQuery({
    queryKey: ['email-log-submission-fields', resolvedSubmissionId, roleContext],
    queryFn: () =>
      fetchSubmissionFields(resolvedSubmissionId!, roleContext as IRoleType),
    enabled: !submissionData && !!resolvedSubmissionId && !!roleContext,
    retry: 0
  });

  const fieldsResponse = submissionData?.fieldsResponse ?? fallbackFieldsResponse;

  const { data: emailLogData, isLoading } = useQuery({
    queryKey: [
      'email-log',
      { submissionContextId, submissionId: resolvedSubmissionId, email, page }
    ],
    queryFn: () =>
      fetchAuthorEmailLogHistory(
        submissionContextId!,
        resolvedSubmissionId!,
        email,
        offset,
        LIMIT
      ),
    enabled: !!submissionContextId && !!resolvedSubmissionId && !!email
  });

  if (!resolvedSubmissionId || !submissionContextId) {
    return (
      <NoRecordFound
        title="Email Log Unavailable"
        subTitle="Email log is only available for submissions."
      />
    );
  }

  if (!fieldsResponse) {
    return isLoadingFallbackFields ? <ListLoader /> : null;
  }

  // Extract and sanitize title
  const rawTitle = fieldsResponse.fields.Title?.value as string | undefined;
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawTitle ?? '', 'text/html');
  const title = doc.body.textContent || '';

  const total = emailLogData?.total || 0;
  const totalPages = total ? Math.ceil(total / LIMIT) : 0;

  if (isLoading) {
    return <ListLoader />;
  }

  if (total === 0) {
    return (
      <NoRecordFound
        title="No Email Log Found"
        subTitle={`There are currently no email logs available.`}
      />
    );
  }

  return (
    <>
      <Text mb={6}>
        Showing{' '}
        <Box as="span" fontWeight="semibold">
          {page} - {totalPages}
        </Box>{' '}
        of{' '}
        <Box as="span" fontWeight="semibold">
          {total}
        </Box>{' '}
        Emails in{' '}
        <Box as="span" fontWeight="semibold">
          {title}
        </Box>
      </Text>

      <Stack spacing={8}>
        {emailLogData?.results.map(log => (
          <EmailLogHistoryTab
            key={log._id}
            emailLog={log}
            name={`${log.firstName ?? ""} ${log.lastName ?? ""}`}
            authorName={authorName}
            email={email}
            roleContext={roleContext}
          />
        ))}
      </Stack>

      <Flex justifyContent={'center'} marginTop={4} width="100%">
        <Pagination
          ariaLabel="Email Log pagination"
          currentPage={page}
          onPageChange={setPage}
          totalPages={totalPages}
        />
      </Flex>
    </>
  );
}
